import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen w-full text-white antialiased bg-[linear-gradient(180deg,_#0d0b18_0%,_#16122c_50%,_#0b0914_100%)] relative overflow-x-hidden">
      {/* Soft, silk-like velvet glow overlay */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none" />

      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Privacy Policy</h1>
              <p className="text-purple-400 text-sm">Your Privacy Matters</p>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Header */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h1 className="text-3xl font-bold text-white mb-4">Privacy Policy</h1>
              <p className="text-gray-400 mb-4">
                Last Updated: June 14, 2026
              </p>
              <p className="text-gray-300">
                SolveICSE ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our educational platform.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">1. Information We Collect</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-white font-medium mb-2">Personal Information</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Name and email address (when you create an account)</li>
                    <li>Academic stream/class information (ICSE/ISC, class level)</li>
                    <li>Profile information you choose to provide</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">Usage Data</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Pages visited and time spent on our platform</li>
                    <li>Resources accessed and downloaded</li>
                    <li>Device information and browser type</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Third-Party Services & Advertising */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">2. Third-Party Services & Advertising</h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  <strong className="text-purple-400">Google AdSense:</strong> We use Google AdSense to display advertisements on our website. Google uses cookies to serve ads based on your prior visits to this website or other websites.
                </p>
                <div>
                  <h3 className="text-white font-medium mb-2">How Google Uses Data</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Google may use cookies to collect information about your browsing activities</li>
                    <li>Data may be used to serve personalized advertisements based on your interests</li>
                    <li>Google's use of advertising cookies enables it and its partners to serve ads to you based on your visit to this site and/or other sites on the Internet</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">Your Ad Choices</h3>
                  <p className="text-gray-400">
                    You can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Google's Ads Settings</a> or by visiting <a href="http://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">www.aboutads.info</a>.
                  </p>
                </div>
              </div>
            </div>

            {/* Cookies and Tracking Technologies */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">3. Cookies and Tracking Technologies</h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  We use cookies and similar tracking technologies to track activity on our website and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier.
                </p>
                <div>
                  <h3 className="text-white font-medium mb-2">Types of Cookies We Use</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
                    <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
                    <li><strong>Advertising Cookies:</strong> Used to deliver relevant advertisements</li>
                  </ul>
                </div>
                <p className="text-gray-400">
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
                </p>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">4. How We Use Your Information</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>To provide, maintain, and improve our educational services</li>
                <li>To personalize your experience and show relevant content</li>
                <li>To communicate with you about updates and new features</li>
                <li>To analyze usage patterns and improve our platform</li>
                <li>To display relevant advertisements through Google AdSense</li>
                <li>To comply with legal obligations and protect our rights</li>
              </ul>
            </div>

            {/* Data Security */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">5. Data Security</h2>
              <p className="text-gray-300">
                We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>

            {/* Your Rights */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">6. Your Rights</h2>
              <p className="text-gray-300 mb-4">You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>Access and update your personal information</li>
                <li>Delete your account and associated data</li>
                <li>Opt out of marketing communications</li>
                <li>Request information about how we use your data</li>
                <li>File a complaint with data protection authorities</li>
              </ul>
            </div>

            {/* Children's Privacy */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">7. Children's Privacy</h2>
              <p className="text-gray-300">
                Our website is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us, and we will delete such information.
              </p>
            </div>

            {/* Changes to This Policy */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">8. Changes to This Privacy Policy</h2>
              <p className="text-gray-300">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
              </p>
            </div>

            {/* Contact Information */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">9. Contact Us</h2>
              <p className="text-gray-300 mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="space-y-2 text-gray-400">
                <p>Email: <a href="mailto:devajithmohanmohan@gmail.com" className="text-purple-400 hover:underline">devajithmohanmohan@gmail.com</a></p>
                <p>Website: <a href="https://solveicse.dev" className="text-purple-400 hover:underline">https://solveicse.dev</a></p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
